import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface StoryPost {
  id: string;
  dish_id: string | null;
  image_url: string | null;
  image_urls: string[] | null;
  video_url: string | null;
  video_urls: string[] | null;
  media_type: 'image' | 'video' | null;
  content: string | null;
  created_at: string;
  user_id: string;
  profiles: { id: string; username: string; avatar_url: string | null } | null;
  likes: { user_id: string }[];
  comments: { id: string }[];
  likes_count: number;
  comment_count: number;
  user_has_liked: boolean;
  dishes: {
    name: string;
    slug: string | null;
    share_count: number | null;
    save_count: number | null;
    restaurants: { name: string } | null;
  } | null;
  rating_distribution: {
    taste_fire_count?: number | null;
    taste_life_changing_count?: number | null;
    taste_solid_count?: number | null;
    taste_meh_count?: number | null;
    value_steal_count?: number | null;
    value_fair_deal_count?: number | null;
    portion_just_right_count?: number | null;
    portion_food_coma_count?: number | null;
    total_reviews?: number | null;
  } | null;
}

export function useDishStoryPosts(dishId: string | null) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['dish-story-posts', dishId],
    staleTime: 60 * 1000,
    enabled: !!dishId,
    queryFn: async (): Promise<StoryPost[]> => {
      const { data: posts, error: postsError } = await supabase
        .from('posts')
        .select('*')
        .eq('dish_id', dishId!)
        .or('image_url.not.is.null,video_url.not.is.null')
        .order('created_at', { ascending: false })
        .limit(50);

      if (postsError) throw postsError;
      if (!posts || posts.length === 0) return [];

      const userIds = [...new Set(posts.map(p => p.user_id))];
      const postIds = posts.map(p => p.id);

      const [profilesRes, likesRes, commentsRes, dishRes, distributionRes] = await Promise.all([
        supabase.from('profiles').select('id, username, avatar_url').in('id', userIds),
        supabase.from('likes').select('post_id, user_id').in('post_id', postIds),
        supabase.from('comments').select('id, post_id').in('post_id', postIds),
        supabase.from('dishes')
          .select('id, name, slug, share_count, save_count, restaurants (name)')
          .eq('id', dishId!)
          .single(),
        supabase.from('dish_rating_distribution').select('*').eq('dish_id', dishId!).maybeSingle(),
      ]);

      const profilesMap = new Map((profilesRes.data || []).map(p => [p.id, p]));
      const dishData = dishRes.data || null;
      const distribution = distributionRes.data || null;

      return posts.map(post => {
        const postLikes = (likesRes.data || []).filter(l => l.post_id === post.id);
        const postComments = (commentsRes.data || []).filter(c => c.post_id === post.id);
        const userHasLiked = user ? postLikes.some(l => l.user_id === user.id) : false;

        return {
          ...post,
          profiles: profilesMap.get(post.user_id) || null,
          likes: postLikes,
          comments: postComments,
          likes_count: postLikes.length,
          comment_count: postComments.length,
          user_has_liked: userHasLiked,
          dishes: dishData as StoryPost['dishes'],
          rating_distribution: distribution,
        };
      });
    },
  });
}
